import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, TextField, Grid, Typography, Checkbox, FormControlLabel, Select, MenuItem } from '@mui/material';
import axios from '../../utils/axios.utils';

interface Crop {
  crop_id: number;
  name: string;
}

interface GlobalMfr {
  global_mfr_id: string;
  mfr_name: string;
}

interface FormValues {
  global_mfr_id: string;
  gtin: string;
  seed_variety_name_mfr: string;
  seed_variety_name_short: string;
  seed_variety_desc: string;
  package: string;
  crop_id: string;
  locked: boolean;
  active: boolean;
}

interface EditMfrSeedFormProps {
  id: string;
  onSuccess: () => void;
  initialData?: any;
  showSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const EditMfrSeedForm: React.FC<EditMfrSeedFormProps> = ({ id, onSuccess, initialData, showSnackbar = () => {} }) => {
  const history = useHistory();
  const [formValues, setFormValues] = useState<FormValues>({
    global_mfr_id: '',
    gtin: '',
    seed_variety_name_mfr: '',
    seed_variety_name_short: '',
    seed_variety_desc: '',
    package: '',
    crop_id: '',
    locked: false,
    active: true,
  });

  const [crops, setCrops] = useState<Crop[]>([]);
  const [globalMfrs, setGlobalMfrs] = useState<GlobalMfr[]>([]);

  useEffect(() => {
    if (initialData) {
      setFormValues(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [cropsRes, mfrsRes] = await Promise.all([
          axios.get<any, any>('api/admin/autotreat_mfr/crops/', {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          }),
          axios.get<any, any>('api/admin/autotreat_mfr/mfrs/', {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          }),
        ]);
        setCrops(cropsRes.data);
        setGlobalMfrs(mfrsRes.data);
      } catch (err: any) {
        console.error('Error loading dropdown data:', err);
        const errorMessage = err?.response?.data?.message || 'Error loading dropdown data';
        showSnackbar(errorMessage, 'error');
      }
    };

    const fetchSeed = async () => {
      try {
        const response = await axios.get<any, any>(`api/admin/autotreat_mfr/mfr_seeds/${id}/`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        });

        const data = response.data;
        setFormValues({
          global_mfr_id: data.global_mfr_id || '',
          gtin: data.gtin || '',
          seed_variety_name_mfr: data.seed_variety_name_mfr || '',
          seed_variety_name_short: data.seed_variety_name_short || '',
          seed_variety_desc: data.seed_variety_desc || '',
          package: data.package || '',
          crop_id: data.crop?.crop_id?.toString() || '',
          locked: data.locked || false,
          active: data.active || true,
        });
      } catch (error: any) {
        console.error('Error fetching seed:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to load seed data.';
        showSnackbar(errorMessage, 'error');
      }
    };

    fetchSeed();
    fetchDropdowns();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = id 
        ? `api/admin/autotreat_mfr/mfr_seeds/${id}/`
        : 'api/admin/autotreat_mfr/mfr_seeds/';
      const method = id ? 'put' : 'post';
      
      await axios[method](url, formValues, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
        }
      });
      
      showSnackbar('Seed updated successfully!', 'success');
      onSuccess();
    } catch (error: any) {
      console.error('Error saving seed:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to save seed. Please try again.';
      showSnackbar(errorMessage, 'error');
    }
  };



  return (
    <form id="dialog-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Select
            value={formValues.global_mfr_id}
            onChange={(e) => handleSelectChange('global_mfr_id', e.target.value as string)}
            fullWidth
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Global MFR ID
            </MenuItem>
            {globalMfrs.map((mfr) => (
              <MenuItem key={mfr.global_mfr_id} value={mfr.global_mfr_id}>{mfr.mfr_name}</MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            name="gtin"
            label="GTIN"
            fullWidth
            variant="outlined"
            value={formValues.gtin || ''}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            name="seed_variety_name_mfr"
            label="Seed Variety Name MFR"
            fullWidth
            variant="outlined"
            value={formValues.seed_variety_name_mfr || ''}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            name="seed_variety_name_short"
            label="Seed Variety Name Short"
            fullWidth
            variant="outlined"
            value={formValues.seed_variety_name_short || ''}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="seed_variety_desc"
            label="Seed Variety Desc"
            fullWidth
            variant="outlined"
            value={formValues.seed_variety_desc || ''}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="package"
            label="Package"
            fullWidth
            variant="outlined"
            value={formValues.package || ''}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            value={formValues.crop_id}
            onChange={(e) => handleSelectChange('crop_id', e.target.value as string)}
            fullWidth
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Crop
            </MenuItem>
            {crops.map((crop) => (
              <MenuItem key={crop.crop_id} value={crop.crop_id.toString()}>{crop.name}</MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Checkboxes */}
        <Grid item xs={12} container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="locked"
                  checked={formValues.locked || false}
                  onChange={handleInputChange}
                />
              }
              label="Locked"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={formValues.active || false}
                  onChange={handleInputChange}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>


      </Grid>
    </form>
  );
};

export default EditMfrSeedForm;